class FilterDrawer extends HTMLElement {
  connectedCallback() {
    this.panelContent = this.querySelector('[data-filter-panel-content]');
    this.collectionGrid = document.querySelector('collection-grid');
    this.sectionId = this.collectionGrid ? this.collectionGrid.dataset.sectionId : null;
    this.baseUrl = this.collectionGrid ? this.collectionGrid.dataset.baseUrl : window.location.pathname;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-filter-close]')) this.close();
    });

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-filter-open]')) this.open();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.hidden) this.close();
    });

    this.#bindPanelEvents();
  }

  #bindPanelEvents() {
    if (!this.panelContent) return;

    // Genre/style checkboxes and artist-list checkboxes auto-apply.
    this.panelContent.addEventListener('change', (event) => {
      if (event.target.matches('input[type="checkbox"]')) {
        this.#applyFilters();
      }
    });

    this.panelContent.addEventListener('click', (event) => {
      const priceApply = event.target.closest('[data-filter-price-apply]');
      if (priceApply) {
        this.#applyFilters();
        return;
      }

      const chipRemove = event.target.closest('[data-artist-chip-remove]');
      if (chipRemove) {
        const chip = chipRemove.closest('[data-artist-chip]');
        const param = chip.dataset.param;
        const value = chip.dataset.value;
        const checkbox = this.panelContent.querySelector(
          `input[type="checkbox"][name="${CSS.escape(param)}"][value="${CSS.escape(value)}"]`
        );
        if (checkbox) checkbox.checked = false;
        this.#applyFilters();
        return;
      }

      const clear = event.target.closest('[data-filter-clear]');
      if (clear) {
        event.preventDefault();
        this.#applyFilters(clear.getAttribute('href'));
      }
    });

    this.panelContent.addEventListener('input', (event) => {
      if (event.target.matches('[data-artist-search]')) {
        this.#filterArtistOptions(event.target.value);
      }
      if (event.target.matches('[data-filter-price-input]')) {
        // Allow Enter key to apply without needing the button.
      }
    });

    this.panelContent.addEventListener('keydown', (event) => {
      if (event.target.matches('[data-filter-price-input]') && event.key === 'Enter') {
        event.preventDefault();
        this.#applyFilters();
      }
    });
  }

  #filterArtistOptions(query) {
    const normalized = query.trim().toLowerCase();
    const options = this.panelContent.querySelectorAll('[data-artist-option]');
    options.forEach((option) => {
      const label = option.dataset.label || '';
      option.hidden = normalized.length > 0 && !label.includes(normalized);
    });
  }

  async #applyFilters(overrideUrl) {
    const form = this.panelContent.querySelector('[data-filters-form]');
    if (!form && !overrideUrl) return;

    let url;
    if (overrideUrl) {
      url = new URL(overrideUrl, window.location.origin);
    } else {
      const formData = new FormData(form);
      const params = new URLSearchParams();
      for (const [key, value] of formData.entries()) {
        if (value !== '') params.append(key, value);
      }
      url = new URL(this.baseUrl, window.location.origin);
      url.search = params.toString();
    }

    this.classList.add('is-loading');

    try {
      const fetchUrl = new URL(url.toString());
      fetchUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Swap the filter panel itself (fresh counts / chips / checked state).
      const newPanelContent = doc.querySelector('[data-filter-panel-content]');
      if (newPanelContent) {
        this.panelContent.innerHTML = newPanelContent.innerHTML;
      }

      // Swap the product grid + infinite-scroll sentinel.
      if (this.collectionGrid) {
        const newItems = doc.querySelector('[data-collection-grid-items]');
        const newSentinel = doc.querySelector('[data-infinite-scroll-sentinel]');
        const currentItems = this.collectionGrid.querySelector('[data-collection-grid-items]');
        const currentSentinel = this.collectionGrid.querySelector('[data-infinite-scroll-sentinel]');

        if (newItems && currentItems) currentItems.innerHTML = newItems.innerHTML;
        if (newSentinel && currentSentinel) {
          currentSentinel.dataset.nextUrl = newSentinel.dataset.nextUrl || '';
        }

        this.collectionGrid.refresh();
      }

      // Keep the URL shareable/bookmarkable and correct on reload.
      window.history.pushState({}, '', url.toString());
    } catch (error) {
      console.error('[filter-drawer] failed to apply filters', error);
    } finally {
      this.classList.remove('is-loading');
    }
  }

  open() {
    this.hidden = false;
    requestAnimationFrame(() => this.classList.add('is-open'));
    document.documentElement.classList.add('filter-drawer-open');
  }

  close() {
    this.classList.remove('is-open');
    document.documentElement.classList.remove('filter-drawer-open');
    const onEnd = () => {
      this.hidden = true;
      this.removeEventListener('transitionend', onEnd);
    };
    this.addEventListener('transitionend', onEnd);
  }
}

if (!customElements.get('filter-drawer')) {
  customElements.define('filter-drawer', FilterDrawer);
}
